/**
 * D349, D350: the compute theme's art, once for both renderers. Every producer's plot, the yard's
 * office and warehouse, each chain's building and each festival's mark is described here as a
 * list of `Unit`s: a footprint on the ground and the simple solids standing on it (`Piece`s:
 * boxes, cylinders, cooling towers, rods, tilted panels, discs), with the marks on a box's faces
 * (rack lights, windows, grilles, glowing bands). The flat map (`iso-kit-compute.ts`) paints
 * them in SVG; the 3D map (`idle/three/compute.ts`) builds them as instanced meshes. Heights are
 * in the 3D map's world units (a tile is one unit across); the flat map turns them into pixels.
 *
 * D350: the campus grows tall as well as wide. A plot's footprint grows with the count (the
 * shared grid fills its tiles), and its buildings stack floors, its racks gain levels and its
 * towers rise with the milestones reached (`tier`); a plot's lights fill as its bar fills
 * (`stage`), and a worked plot's LEDs turn cyan (`sweepOn`). D368: rack and node LEDs each blink
 * on their own seeded rhythm rather than together, and a worked plot's own cycle (`busyShare`)
 * makes its blinking denser and faster, not a single on/off flip.
 */
import type { IsoBuildingAt } from './IsoWorldMap.tsx';
import { unionBounds, type Bounds, type IsoParcel, type TileKey } from './iso.ts';

/* ---------- the pieces ---------- */

/** What lights a box's faces: rack lights, windows, grilles or a glowing band. */
export interface FaceMarks {
  kind: 'led' | 'win' | 'grille' | 'band';
  rows: number;
  cols: number;
  /** The marks' own colour, unlit. */
  c: string;
  /** Their colour lit: a rack light on, a window lit at night, a band's glow. */
  on?: string;
  /** The share of rack lights on, 0 to 1 (the bar's fill); D381: of windows lit at night. */
  lit?: number;
  /** Some of the lights blink. */
  blink?: boolean;
  /** D368: how busy the unit's own work cycle is, 0 idle to 1 working: more LEDs blink, and
   *  faster, the busier it is. Each blinking LED or small group keeps its own seeded rhythm;
   *  they never all turn on or off together (`busyShare`). */
  busy?: number;
  /** Only the two faces towards the default view (a rack's front); windows go all round. */
  front?: boolean;
  /** The part of the face's height the marks fill, 0 to 1. */
  z0?: number;
  z1?: number;
}

/**
 * D382: what a wall is made of, as the 3D map draws it from its facade atlas: insulated
 * cladding panels, a ribbon window band, curtain glass, punched windows, a louvre bank,
 * profiled sheet, a rack's perforated door, a roller shutter, a personnel door, a signage
 * fascia. The flat map, the light version, keeps the box's marks instead.
 */
export type Facade =
  | 'clad'
  | 'band'
  | 'curtain'
  | 'punched'
  | 'louvre'
  | 'ribbed'
  | 'rack'
  | 'shutter'
  | 'door'
  | 'sign';

export type Piece =
  | {
      s: 'box';
      x: number;
      y: number;
      w: number;
      d: number;
      z: number;
      h: number;
      c: string;
      /** The roof's colour; a little lighter than the walls when left out. */
      top?: string;
      marks?: FaceMarks;
      /** Unlit, always bright: a screen, a glowing strip. */
      glow?: boolean;
      /** Takes snow on its top in winter. On for anything wider than a post. */
      roof?: boolean;
      /** D382: the facade on its walls in the 3D map; its window and grille marks then go. */
      face?: Facade;
    }
  | {
      s: 'cyl';
      x: number;
      y: number;
      r: number;
      z: number;
      h: number;
      c: string;
      top?: string;
      glow?: boolean;
    }
  /** A cooling tower: a waisted shell, open at the top. */
  | { s: 'tower'; x: number; y: number; r: number; z: number; h: number; c: string }
  | {
      s: 'rod';
      a: [number, number, number];
      b: [number, number, number];
      /** Thickness, in world units. */
      t: number;
      c: string;
      glow?: boolean;
    }
  /** A panel tilted up towards the back (-y): a solar panel, a canopy, a plaque. */
  | { s: 'panel'; x: number; y: number; w: number; d: number; z: number; rise: number; c: string }
  | {
      s: 'disc';
      x: number;
      y: number;
      r: number;
      z: number;
      c: string;
      glow?: boolean;
      /** D381: a fan's shroud; the 3D map turns blades over it on the render clock. */
      fan?: boolean;
    }
  /** A round crown: a tree's, a bush's. Its foot at `z`, `r` its radius. */
  | { s: 'ball'; x: number; y: number; r: number; z: number; c: string };

/** A footprint and what stands on it, drawn together, bottom to top. */
export interface Unit {
  x: number;
  y: number;
  w: number;
  d: number;
  pieces: Piece[];
  /**
   * D381: fine detail only the 3D map draws (parapets, insulators, a palisade): the flat map
   * may leave it out, being the light version, and nothing in it contradicts the pieces.
   */
  fine?: Piece[];
  /** The producer it belongs to, for a tap. */
  id?: string;
  /**
   * D345: the ready badge over a plot, at the plot's middle and `lift` over the ground (clear
   * of its tallest building). Its unit stands at the plot's front corner, so the flat map
   * paints it after everything on the plot.
   */
  badge?: { x: number; y: number; id: string; icon: string; lift: number };
}

/** What a model needs beyond the plot itself. */
export interface Look {
  night: boolean;
  winter: boolean;
}

/* ---------- the theme's colours (original, D9) ---------- */

export const C = {
  concrete: '#C9C6BE',
  pad: '#B7B4AC',
  tarmac: '#4C4F55',
  paving: '#D9D3C6',
  gravel: '#B3ADA0',
  line: '#F2EFE6',
  yellow: '#E8C547',
  rack: '#2B2F36',
  rackDoor: '#3A3F48',
  rackLed: '#2F4A3A',
  green: '#5BE37D',
  amber: '#FFB547',
  blue: '#55B6FF',
  cyan: '#5EE6FF',
  glow: '#7CFFB2',
  red: '#FF5A5A',
  violet: '#B48CFF',
  white: '#E9ECEF',
  light: '#C7CCD2',
  mid: '#9AA1AA',
  dark: '#3B4048',
  black: '#24272C',
  glass: '#7FA6C4',
  glassTop: '#A9C6DC',
  window: '#6F93AF',
  lit: '#FFD27A',
  brick: '#A5583F',
  copper: '#B87333',
  roof: '#8E949C',
  steel: '#7E858C',
  wood: '#A87D52',
  laptop: '#5A5F66',
  screen: '#9FD8FF',
  tray: '#E0B83A',
  fibre: '#F2A93B',
  solar: '#2C4A78',
  solarLine: '#8FB0D8',
  drum: '#C7742E',
  vapour: '#F4F6F8',
  pond: '#5E8FA8',
  gold: '#D9B24A',
  fence: '#8C949C',
  van: '#E9ECEF',
  /** D381: louvres, doors, generator sets and their stacks, insulators, a palisade. */
  louvre: '#AEB4BA',
  door: '#5A6068',
  doorLit: '#FFE3A8',
  gen: '#D8D2BF',
  genTop: '#C4BEAB',
  flue: '#3A3D42',
  insulator: '#C9B9A0',
  blade: '#B9BEC4',
  palisade: '#5F7A68',
  /** D382: plinths, signage fascias, rack floors and canopies, roof walkways. */
  plinth: '#A8A49B',
  sign: '#2E3238',
  floor: '#DDE0E3',
  canopy: '#D5D9DD',
  walkway: '#CFC8B8',
  lobby: '#9FB7C9',
  pipeCold: '#4F86D9',
  pipeHot: '#C4532E',
  /** D381: life on the campus, 3D only: gulls over the roofs, the generators' exhaust. */
  gull: '#F1F2F0',
  gullWing: '#AEB6BE',
  exhaust: '#C9CCD0',
} as const;

/**
 * D381: the business park round a campus, once for both renderers: offices' facades by style
 * (glass, brick, cladding), the sheds' profiled cladding, the parked cars' paint.
 */
export const PARK = {
  glass: ['#6F9BC0', '#5E8CB0', '#7BA9BE', '#4F7EA6'],
  brick: ['#A5583F', '#9A4E3A', '#B4694A', '#8E5A48'],
  clad: ['#E6DCC6', '#B8C9B0', '#D9C3A0', '#A9BCCB', '#E2E4E6'],
  shed: ['#A9B4C0', '#9DB4A6', '#C8BCA0', '#8FA4BE'],
  frame: '#E9ECEF',
  sill: '#3F4A56',
  pane: '#4F6F8A',
  roofs: ['#8E949C', '#9AA1AA', '#7C838B', '#A9AFB6'],
  plant: ['#C7CCD2', '#9AA1AA'],
  door: '#5A6068',
  rooflight: '#D6E4EE',
  /** The lawns and verges, and the green the campus's grass leans to. */
  lawn: '#7FB35A',
  lamp: '#5A6068',
  cars: ['#E9ECEF', '#C4382E', '#2E5F9E', '#2C3038', '#9AA1AA', '#E0B83A', '#3E7A46', '#B7BCC2'],
  cabin: '#33404D',
} as const;

/** D291: the building decorations, the compute theme's cladding and roofs. */
export const CLADDING: Record<string, string> = {
  cladding_white: '#EEF0F2',
  cladding_mural: '#6A7FD0',
  cladding_brick: '#A5583F',
  cladding_black: '#2E3238',
  cladding_living: '#5E8F4A',
  cladding_glass: '#7FA6C4',
  cladding_copper: '#B87333',
};
export const ROOFING: Record<string, string> = {
  roof_grey: '#7C838B',
  roof_solar: '#2C4A78',
  roof_green: '#6F9B4E',
  roof_white: '#F2F3F4',
};
/** D331: the boundary decorations colour the campus fence; the gate's the entrance sign. */
export const FENCING: Record<string, string> = {
  racks_black: '#33373D',
  racks_white: '#E4E7EA',
  racks_blue: '#3E6FB0',
  racks_orange: '#D9752B',
  racks_glass: '#9DC0D8',
  racks_stickers: '#C2508A',
};
/**
 * D420: a campus has no pens, so its fence decoration fills the campus fence's panels: a wire
 * mesh (the default, the fence's own pale mesh), steel palisade, white rail, a timber screen,
 * green mesh, a stone wall, a glass screen.
 */
export const FENCE_INFILL: Record<string, string> = {
  fence_palisade: '#4A4F58',
  fence_white: '#F2F3F4',
  fence_timber: '#A87D52',
  fence_green: '#4F8A5A',
  fence_stone: '#A8A49B',
  fence_glass: '#BFDDEA',
};
export const SIGNS: Record<string, string> = {
  sign_white: '#F2F3F4',
  sign_neon: '#FF4FD8',
  sign_steel: '#9AA1AA',
  sign_pixel: '#4FB0FF',
};
/**
 * D380: who calls at the campus gate, by the data's visitor id: the coat, a trim colour, and the
 * van or car they came in, if any. The 3D map draws them; the flat map shows no visitor.
 */
export const CAMPUS_VISITOR: Record<
  string,
  { coat: string; trim: string; ride?: { body: string; long: number; tall: number } }
> = {
  auditor: { coat: '#33404D', trim: '#E9ECEF' },
  courier: { coat: '#D9752B', trim: '#F2EFE6', ride: { body: '#E9ECEF', long: 0.6, tall: 0.3 } },
  client: { coat: '#4A5E78', trim: '#9AA1AA', ride: { body: '#2C3038', long: 0.46, tall: 0.18 } },
  safety_inspector: { coat: '#E8C547', trim: '#F2EFE6' },
  journalist: { coat: '#7A4E8E', trim: '#E9ECEF' },
};
/** D380: a spare part found on the yard: a crate with a board and a chip glinting in it. */
export const CAMPUS_FIND = { crate: '#8A8F96', board: '#2F7A52', chip: '#F2C94C' } as const;
export const VANS: Record<string, string> = {
  van_red: '#C4382E',
  van_green: '#3E7A46',
  van_blue: '#2E5F9E',
  van_yellow: '#E0B83A',
  van_orange: '#D9752B',
  van_vintage: '#8FB3A2',
  van_show_wrap: '#B048C8',
};
/** D331: the staff's kit, the hands decoration. */
export const KIT: Record<string, string> = {
  kit_tshirt: '#4A78B8',
  kit_check: '#A0453A',
  kit_hi_vis: '#E6E23A',
  kit_hoodie: '#4A4F58',
  kit_fleece: '#3E7A6A',
  kit_suit: '#2C3038',
  kit_gold: '#D9B24A',
};
/** D331: a cable decoration lays the yard's painted lines in its colour. */
export const CABLES: Record<string, string> = {
  cables_grey: '#9AA1AA',
  cables_blue: '#4F86D9',
  cables_yellow: '#E8C547',
  cables_red: '#D9473A',
  cables_green: '#4FAF62',
  cables_rainbow: '#C060D0',
  cables_gold: '#D9B24A',
};

/**
 * D349: how far winter whitens the campus: its flat roofs (they hold snow better than the
 * farm's pitched ones), its grass and its hard ground.
 */
export const WINTER = { roof: 0.85, grass: 0.65, hard: 0.4 } as const;

/** A storey's height, in world units. */
export const FLOOR = 0.3;

/* ---------- small helpers ---------- */

/** A number 0..1 from a place, the same on every device. */
export function hashXY(x: number, y: number, salt = 0): number {
  let h =
    (Math.imul(Math.round(x * 97) | 0, 0x27d4eb2d) ^
      Math.imul(Math.round(y * 89) | 0, 0x165667b1) ^
      salt) >>>
    0;
  h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d);
  h = Math.imul(h ^ (h >>> 12), 0x297a2d39);
  return ((h ^ (h >>> 15)) >>> 0) / 4294967296;
}

export const steps = (tier: number, per: number, cap: number): number =>
  Math.max(0, Math.min(cap, Math.floor(tier / per)));

export function tilesOf(p: IsoParcel): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  for (const k of p.tiles) {
    const [a, b] = k.split(',');
    const x = Number(a),
      y = Number(b);
    if (Number.isFinite(x) && Number.isFinite(y)) out.push([x, y]);
  }
  return out;
}

/**
 * The biggest rectangle of the plot's tiles from its back corner: where its main building
 * stands. The rest of its tiles take the plot's smaller kit.
 */
export function mainRect(tiles: readonly TileKey[]): Bounds {
  const set = new Set(tiles);
  const b = unionBounds(tiles);
  let best: Bounds = { x: b.x, y: b.y, w: 0, h: 0 };
  for (let w = 1; w <= b.w; w++) {
    let h = 0;
    while (h < b.h) {
      let ok = true;
      for (let i = 0; i < w && ok; i++) if (!set.has(`${b.x + i},${b.y + h}`)) ok = false;
      if (!ok) break;
      h++;
    }
    if (w * h > best.w * best.h) best = { x: b.x, y: b.y, w, h };
  }
  return best;
}

/** D353: whether a worked plot's racks are lit now: the whole plot together, while its own
 *  cycle (`atCycle`, which set `stage`) is ripe. */
export function sweepOn(p: IsoParcel): boolean {
  return !!p.cycle && p.stage === 5;
}

/** The share of a plot's lights on: its bar's fill, or all of them in a sweep or when ready. */
export function litShare(p: IsoParcel): number {
  // D454: `ready` is any marker; only a due one (a full bar) lights the lot.
  if (p.cycle || (p.ready && p.due !== false)) return 1;
  return Math.max(0.15, (p.stage ?? 0) / 5);
}

/** D368: how busy a plot's own work cycle looks: fully busy while it is actively cycling, a low
 *  background flicker otherwise. Drives how dense and how fast its LEDs blink (never a single
 *  on/off flip for the whole plot). */
export function busyShare(p: IsoParcel): number {
  return p.cycle ? 1 : 0.15;
}

/** A box helper: a footprint inset by `m` on every side. */
export function inset(x: number, y: number, w: number, d: number, m: number) {
  return { x: x + m, y: y + m, w: Math.max(0.05, w - 2 * m), d: Math.max(0.05, d - 2 * m) };
}

/* ---------- the producers ---------- */

type Model = (p: IsoParcel, L: Look) => Unit[];

/** One rack, standing at (x, y), `w` across the front, its lights on that front. */
function rack(
  p: IsoParcel,
  x: number,
  y: number,
  w: number,
  d: number,
  h: number,
  led: string,
  body: string = C.rack,
  z = 0,
): Piece[] {
  return [
    {
      s: 'box',
      x,
      y,
      w,
      d,
      z,
      h,
      c: body,
      top: C.dark,
      face: 'rack',
      marks: {
        kind: 'led',
        rows: Math.max(2, Math.round(h / 0.22)),
        cols: 2,
        c: C.rackLed,
        on: sweepOn(p) ? C.cyan : led,
        lit: litShare(p),
        blink: true,
        busy: busyShare(p),
        front: true,
        z0: 0.1,
        z1: 0.92,
      },
    },
  ];
}

/** A building of `floors` storeys on a footprint, windows all round, plant on the roof. */
export function block(
  x: number,
  y: number,
  w: number,
  d: number,
  floors: number,
  wall: string,
  roof: string,
  win: string = C.window,
  winLit: string = C.lit,
  z = 0,
  face: Facade = 'band',
): Piece[] {
  const h = floors * FLOOR + 0.08;
  return [
    // D382: on the ground, a building stands on a plinth a little wider than its walls.
    ...(z === 0 ? [plinth(x, y, w, d)] : []),
    {
      s: 'box',
      x,
      y,
      w,
      d,
      z,
      h,
      c: wall,
      top: roof,
      roof: true,
      face,
      marks: {
        kind: 'win',
        rows: floors,
        cols: Math.max(1, Math.round(Math.max(w, d) * 3)),
        c: win,
        on: winLit,
        z0: 0.06,
        z1: 0.96,
      },
    },
  ];
}

/**
 * D382: a cage round equipment on a floor: corner posts, top rails and a ladder of cable tray
 * across the top, so cabinets in the open read as a fitted-out bay. Fine detail.
 */
export function cage(x: number, y: number, w: number, d: number, h: number): Piece[] {
  const out: Piece[] = [];
  const c = C.steel;
  for (const [px, py] of [
    [x, y],
    [x + w, y],
    [x, y + d],
    [x + w, y + d],
  ] as const)
    out.push({ s: 'rod', a: [px, py, 0], b: [px, py, h], t: 0.022, c });
  out.push(
    { s: 'rod', a: [x, y, h], b: [x + w, y, h], t: 0.018, c },
    { s: 'rod', a: [x, y + d, h], b: [x + w, y + d, h], t: 0.018, c },
    { s: 'rod', a: [x, y, h], b: [x, y + d, h], t: 0.018, c },
    { s: 'rod', a: [x + w, y, h], b: [x + w, y + d, h], t: 0.018, c },
    { s: 'rod', a: [x, y, h * 0.5], b: [x, y + d, h * 0.5], t: 0.012, c },
    { s: 'rod', a: [x, y, h * 0.5], b: [x + w, y, h * 0.5], t: 0.012, c },
  );
  out.push({
    s: 'box',
    x: x + 0.05,
    y: y + d * 0.45,
    w: w - 0.1,
    d: 0.1,
    z: h,
    h: 0.012,
    c: C.tray,
  });
  return out;
}

/** D382: the low plinth a building stands on, proud of its walls. */
export function plinth(x: number, y: number, w: number, d: number): Piece {
  return {
    s: 'box',
    x: x - 0.03,
    y: y - 0.03,
    w: w + 0.06,
    d: d + 0.06,
    z: 0,
    h: 0.045,
    c: C.plinth,
  };
}

/**
 * Roof plant: a few fans and a unit, on a roof at height `z`. D381: a roof wide enough takes an
 * air-handling unit along its back, louvred, its fans on top; every fan turns in the 3D map.
 */
export function roofPlant(
  x: number,
  y: number,
  w: number,
  d: number,
  z: number,
  n: number,
): Piece[] {
  const out: Piece[] = [];
  if (n >= 2 && w >= 0.9 && d >= 0.6) {
    const aw = Math.min(w * 0.6, 1.1),
      ad = 0.26;
    const ax = x + (w - aw) / 2;
    out.push({
      s: 'box',
      x: ax,
      y,
      w: aw,
      d: ad,
      z,
      h: 0.15,
      c: C.light,
      top: C.mid,
      roof: true,
      face: 'louvre',
      marks: {
        kind: 'grille',
        rows: 1,
        cols: Math.max(2, Math.round(aw * 4)),
        c: C.louvre,
        front: true,
        z0: 0.2,
        z1: 0.85,
      },
    });
    const fans = Math.max(1, Math.floor(aw / 0.3));
    for (let i = 0; i < fans; i++)
      out.push({
        s: 'disc',
        x: ax + ((i + 0.5) * aw) / fans,
        y: y + ad / 2,
        r: 0.09,
        z: z + 0.152,
        c: C.dark,
        fan: true,
      });
    y += ad + 0.06;
    d -= ad + 0.06;
    n -= 2;
  }
  const cols = Math.max(1, Math.min(n, Math.floor(w / 0.38)));
  const rows = Math.max(1, Math.ceil(n / cols));
  for (let i = 0; i < n; i++) {
    const cx = x + (((i % cols) + 0.5) * w) / cols,
      cy = y + ((Math.floor(i / cols) + 0.5) * d) / rows;
    // D382: a condenser in its louvred casing, its fan on top, on a low frame.
    out.push({ s: 'box', x: cx - 0.14, y: cy - 0.12, w: 0.28, d: 0.24, z, h: 0.025, c: C.steel });
    out.push({
      s: 'box',
      x: cx - 0.13,
      y: cy - 0.11,
      w: 0.26,
      d: 0.22,
      z: z + 0.025,
      h: 0.09,
      c: C.light,
      top: C.mid,
      face: 'louvre',
    });
    out.push({ s: 'disc', x: cx, y: cy, r: 0.09, z: z + 0.117, c: C.dark, fan: true });
  }
  return out;
}

/** D381: a parapet round a flat roof at height `z`: a low upstand on each edge. Fine detail. */
export function parapet(x: number, y: number, w: number, d: number, z: number, c: string): Piece[] {
  const t = 0.035,
    h = 0.06;
  // D382: a pale metal capping along its top.
  const top = C.white;
  return [
    { s: 'box', x, y, w, d: t, z, h, c, top },
    { s: 'box', x, y: y + d - t, w, d: t, z, h, c, top },
    { s: 'box', x, y: y + t, w: t, d: d - 2 * t, z, h, c, top },
    { s: 'box', x: x + w - t, y: y + t, w: t, d: d - 2 * t, z, h, c, top },
  ];
}

/**
 * D382: what a flat roof carries beyond its plant: a walkway from the hatch to the plant, the
 * hatch, a row of rooflights on a long roof. Fine detail.
 */
export function roofDetail(x: number, y: number, w: number, d: number, z: number): Piece[] {
  const out: Piece[] = [
    { s: 'box', x: x + 0.12, y: y + d - 0.2, w: w - 0.24, d: 0.07, z, h: 0.008, c: C.walkway },
    { s: 'box', x: x + 0.12, y: y + d - 0.32, w: 0.1, d: 0.1, z, h: 0.05, c: C.mid },
  ];
  if (w >= 1.4)
    for (let i = 0; i < Math.floor((w - 0.6) / 0.45); i++)
      out.push({
        s: 'box',
        x: x + 0.4 + i * 0.45,
        y: y + d - 0.12,
        w: 0.22,
        d: 0.06,
        z,
        h: 0.03,
        c: C.glassTop,
      });
  return out;
}

/** D381: a hall's doors on its front (south) face: a roller door and a door beside it. */
export function doors(x: number, y: number, w: number, d: number): Piece[] {
  const rw = Math.min(0.34, w * 0.3);
  const rx = x + w * 0.62;
  return [
    {
      s: 'box',
      x: rx,
      y: y + d - 0.005,
      w: rw,
      d: 0.025,
      z: 0,
      h: 0.22,
      c: C.door,
      face: 'shutter',
      marks: { kind: 'grille', rows: 4, cols: 1, c: C.flue, front: true },
    },
    {
      s: 'box',
      x: rx - 0.16,
      y: y + d - 0.005,
      w: 0.09,
      d: 0.025,
      z: 0,
      h: 0.17,
      c: C.door,
      face: 'door',
    },
  ];
}

/** D381: a standby generator set in its enclosure, louvred, its exhaust stack beside it. */
export function generator(x: number, y: number): Piece[] {
  return [
    { s: 'box', x: x + 0.06, y: y + 0.18, w: 0.72, d: 0.56, z: 0, h: 0.035, c: C.pad },
    {
      s: 'box',
      x: x + 0.12,
      y: y + 0.24,
      w: 0.6,
      d: 0.44,
      z: 0,
      h: 0.3,
      c: C.gen,
      top: C.genTop,
      roof: true,
      face: 'louvre',
      marks: { kind: 'grille', rows: 2, cols: 3, c: C.louvre, front: true, z0: 0.2, z1: 0.8 },
    },
    { s: 'box', x: x + 0.2, y: y + 0.34, w: 0.26, d: 0.2, z: 0.3, h: 0.06, c: C.mid },
    { s: 'cyl', x: x + 0.8, y: y + 0.36, r: 0.045, z: 0, h: 0.68, c: C.flue, top: C.black },
    {
      s: 'rod',
      a: [x + 0.72, y + 0.36, 0.22],
      b: [x + 0.8, y + 0.36, 0.22],
      t: 0.05,
      c: C.flue,
    },
  ];
}

/** Laptops: desks in a garage at first, the garage growing into a start-up's office. */
const laptops: Model = (p) => {
  const tier = p.tier ?? 0;
  const out: Unit[] = [];
  const T = tilesOf(p);
  const first = T[0];
  if (!first) return out;
  const floors = 1 + steps(tier, 3, 4);
  const [gx, gy] = first;
  // The garage: a roller door on its front, a floor more as the start-up grows.
  const g: Piece[] = [
    {
      s: 'box',
      x: gx + 0.08,
      y: gy + 0.08,
      w: 0.84,
      d: 0.84,
      z: 0,
      h: 0.42,
      c: '#D8CFBF',
      top: C.roof,
      roof: true,
      face: 'clad',
    },
    {
      s: 'box',
      x: gx + 0.2,
      y: gy + 0.9,
      w: 0.56,
      d: 0.03,
      z: 0,
      h: 0.32,
      c: '#B9BCC0',
      face: 'shutter',
      marks: { kind: 'grille', rows: 5, cols: 1, c: '#9DA1A6' },
    },
  ];
  if (floors > 1)
    g.push(
      ...block(
        gx + 0.12,
        gy + 0.12,
        0.76,
        0.76,
        floors - 1,
        C.white,
        C.roof,
        C.window,
        C.lit,
        0.42,
      ),
    );
  out.push({ x: gx, y: gy, w: 1, d: 1, pieces: g, id: p.id });
  // A desk per tile, two laptops open on it, screens glowing.
  for (const [x, y] of T.slice(1)) {
    const pieces: Piece[] = [];
    const dy = 0.3;
    pieces.push({
      s: 'box',
      x: x + 0.45,
      y: y + dy + 0.1,
      w: 0.1,
      d: 0.1,
      z: 0,
      h: 0.2,
      c: C.dark,
    });
    pieces.push({
      s: 'box',
      x: x + 0.12,
      y: y + dy,
      w: 0.76,
      d: 0.34,
      z: 0.2,
      h: 0.04,
      c: C.wood,
      roof: true,
    });
    for (const dx of [0.2, 0.58]) {
      pieces.push({
        s: 'box',
        x: x + dx,
        y: y + dy + 0.12,
        w: 0.2,
        d: 0.14,
        z: 0.24,
        h: 0.015,
        c: C.laptop,
      });
      pieces.push({
        s: 'box',
        x: x + dx,
        y: y + dy + 0.1,
        w: 0.2,
        d: 0.015,
        z: 0.25,
        h: 0.12,
        c: C.laptop,
        marks: { kind: 'band', rows: 1, cols: 1, c: C.screen, on: C.screen, z0: 0.15, z1: 0.9 },
      });
    }
    out.push({ x, y, w: 1, d: 1, pieces, id: p.id });
  }
  return out;
};

/** Home servers: tower cases on shelving, the shelves climbing with each milestone. */
const homeServers: Model = (p) => {
  const tier = p.tier ?? 0;
  const levels = 1 + steps(tier, 2, 6);
  return tilesOf(p).map(([x, y]) => {
    const pieces: Piece[] = [];
    for (let l = 0; l < levels; l++) {
      const z = l * 0.2;
      pieces.push({
        s: 'box',
        x: x + 0.12,
        y: y + 0.2,
        w: 0.76,
        d: 0.6,
        z: z + 0.17,
        h: 0.03,
        c: C.steel,
        roof: l === levels - 1,
      });
      for (const dx of [0.2, 0.56])
        pieces.push(...rack(p, x + dx, y + 0.3, 0.22, 0.4, 0.16, C.blue, '#3C4048', z));
    }
    for (const [px, py] of [
      [0.12, 0.2],
      [0.85, 0.2],
      [0.12, 0.77],
      [0.85, 0.77],
    ] as const)
      pieces.push({
        s: 'box',
        x: x + px,
        y: y + py,
        w: 0.03,
        d: 0.03,
        z: 0,
        h: levels * 0.2,
        c: C.steel,
      });
    return { x, y, w: 1, d: 1, pieces, id: p.id };
  });
};

/**
 * Racks: rows of server racks, lights blinking, a mezzanine of racks more as it grows. D382:
 * housed, not left in the open: each tile is a bay of a rack hall cut away on the two sides
 * the eye sees, on a raised floor, two rows facing across a contained aisle with its glazed
 * roof and end door, a cable tray over each row, steel columns at the corners and a slatted
 * canopy over the top level, so the rows read as equipment under cover.
 */
const racks: Model = (p) => {
  const tier = p.tier ?? 0;
  const levels = 1 + steps(tier, 4, 3);
  const own = new Set(p.tiles);
  const top = levels * 0.72 + 0.02;
  return tilesOf(p).map(([x, y]) => {
    const pieces: Piece[] = [
      { s: 'box', x: x + 0.02, y: y + 0.02, w: 0.96, d: 0.96, z: 0, h: 0.04, c: C.floor },
    ];
    for (let l = 0; l < levels; l++) {
      const z = 0.04 + l * 0.72;
      if (l > 0)
        pieces.push({
          s: 'box',
          x: x + 0.02,
          y: y + 0.02,
          w: 0.96,
          d: 0.96,
          z: z - 0.03,
          h: 0.03,
          c: C.steel,
          top: '#A6ADB4',
        });
      for (const dy of [0.12, 0.56])
        pieces.push(...rack(p, x + 0.1, y + dy, 0.8, 0.3, 0.62, C.green, C.rack, z));
      // The contained aisle between the rows: its glazed roof and its door at the end.
      pieces.push(
        { s: 'box', x: x + 0.1, y: y + 0.42, w: 0.8, d: 0.14, z: z + 0.6, h: 0.02, c: C.glassTop },
        {
          s: 'box',
          x: x + 0.88,
          y: y + 0.42,
          w: 0.02,
          d: 0.14,
          z,
          h: 0.6,
          c: C.glass,
          face: 'door',
        },
      );
      // A cable tray over each row.
      for (const dy of [0.2, 0.64])
        pieces.push({
          s: 'box',
          x: x + 0.06,
          y: y + dy,
          w: 0.88,
          d: 0.14,
          z: z + 0.66,
          h: 0.015,
          c: C.tray,
        });
    }
    // The hall's back and west walls where the plot ends; open to the front and east.
    const fine: Piece[] = [];
    if (!own.has(`${x},${y - 1}`))
      pieces.push({
        s: 'box',
        x,
        y,
        w: 1,
        d: 0.04,
        z: 0,
        h: top,
        c: C.light,
        roof: true,
        face: 'clad',
      });
    if (!own.has(`${x - 1},${y}`))
      pieces.push({
        s: 'box',
        x,
        y,
        w: 0.04,
        d: 1,
        z: 0,
        h: top,
        c: C.light,
        roof: true,
        face: 'clad',
      });
    for (const [px, py] of [
      [0.02, 0.02],
      [0.95, 0.02],
      [0.02, 0.95],
      [0.95, 0.95],
    ] as const)
      pieces.push({
        s: 'box',
        x: x + px,
        y: y + py,
        w: 0.035,
        d: 0.035,
        z: 0,
        h: top,
        c: C.steel,
      });
    // The canopy: slats over the top level, the rows showing between them.
    pieces.push({ s: 'box', x, y: y + 0.02, w: 1, d: 0.05, z: top, h: 0.04, c: C.steel });
    pieces.push({ s: 'box', x, y: y + 0.93, w: 1, d: 0.05, z: top, h: 0.04, c: C.steel });
    for (let i = 0; i < 4; i++)
      fine.push({
        s: 'box',
        x: x + 0.02 + i * 0.25,
        y: y + 0.02,
        w: 0.11,
        d: 0.96,
        z: top + 0.04,
        h: 0.02,
        c: C.canopy,
        roof: true,
      });
    return { x, y, w: 1, d: 1, pieces, fine, id: p.id };
  });
};

/** Switches: wide network cabinets with rows of tiny lights and coloured patch leads. */
const switches: Model = (p) => {
  const tier = p.tier ?? 0;
  const stack = 1 + steps(tier, 3, 4);
  return tilesOf(p).map(([x, y]) => {
    // D382: the cabinets stand on a raised floor.
    const pieces: Piece[] = [
      { s: 'box', x: x + 0.06, y: y + 0.1, w: 0.88, d: 0.78, z: 0, h: 0.04, c: C.floor },
    ];
    for (let s = 0; s < stack; s++) {
      const z = 0.04 + s * 0.16;
      pieces.push({
        s: 'box',
        x: x + 0.12,
        y: y + 0.18,
        w: 0.76,
        d: 0.6,
        z,
        h: 0.14,
        c: '#3E434C',
        top: '#555B65',
        roof: s === stack - 1,
        face: 'rack',
        marks: {
          kind: 'led',
          rows: 2,
          cols: 5,
          c: C.rackLed,
          on: sweepOn(p) ? C.cyan : s % 2 ? C.amber : C.green,
          lit: litShare(p),
          blink: true,
          busy: busyShare(p),
          front: true,
          z0: 0.2,
          z1: 0.85,
        },
      });
    }
    const fine = cage(x + 0.07, y + 0.12, 0.86, 0.74, 0.04 + stack * 0.16 + 0.12);
    const leads = [C.blue, C.yellow, C.red, C.green];
    leads.forEach((c, i) =>
      pieces.push({
        s: 'rod',
        a: [x + 0.2 + i * 0.16, y + 0.8, stack * 0.16 - 0.01],
        b: [x + 0.25 + i * 0.16, y + 0.92, 0.02],
        t: 0.025,
        c,
      }),
    );
    return { x, y, w: 1, d: 1, pieces, fine, id: p.id };
  });
};

/** Fibre links: yellow fibre trays on posts across the plot, a tray more above each few tiers. */
const fibreLinks: Model = (p) => {
  const tier = p.tier ?? 0;
  const trays = 1 + steps(tier, 3, 4);
  return tilesOf(p).map(([x, y]) => {
    const pieces: Piece[] = [];
    for (const px of [0.15, 0.85])
      pieces.push({
        s: 'box',
        x: x + px - 0.03,
        y: y + 0.47,
        w: 0.06,
        d: 0.06,
        z: 0,
        h: 0.2 + trays * 0.16,
        c: C.steel,
      });
    for (let t = 0; t < trays; t++) {
      const z = 0.2 + t * 0.16;
      pieces.push({
        s: 'box',
        x,
        y: y + 0.38,
        w: 1,
        d: 0.24,
        z,
        h: 0.05,
        c: t % 2 ? C.fibre : C.tray,
        top: t % 2 ? '#F7C267' : '#F0D06A',
        roof: t === trays - 1,
      });
    }
    // A splice box at the foot, its lights showing the link at work.
    pieces.push({
      s: 'box',
      x: x + 0.38,
      y: y + 0.7,
      w: 0.24,
      d: 0.16,
      z: 0,
      h: 0.18,
      c: '#5B6068',
      marks: {
        kind: 'led',
        rows: 1,
        cols: 3,
        c: C.rackLed,
        on: C.fibre,
        lit: litShare(p),
        blink: true,
        busy: busyShare(p),
        front: true,
      },
    });
    return { x, y, w: 1, d: 1, pieces, id: p.id };
  });
};

/** Storage arrays: chunky pale cabinets of drive bays with blue status lights. */
const storage: Model = (p) => {
  const tier = p.tier ?? 0;
  const h = 0.5 + steps(tier, 2, 6) * 0.14;
  return tilesOf(p).map(([x, y]) => {
    // D382: three cabinets side by side on a raised floor in a cage, not one block.
    const pieces: Piece[] = [
      { s: 'box', x: x + 0.05, y: y + 0.09, w: 0.9, d: 0.82, z: 0, h: 0.03, c: C.floor },
    ];
    for (const dx of [0.1, 0.38, 0.66]) {
      pieces.push({
        s: 'box',
        x: x + dx,
        y: y + 0.16,
        w: 0.24,
        d: 0.66,
        z: 0.03,
        h,
        c: '#D7DBE0',
        top: '#C0C6CD',
        roof: true,
        face: 'rack',
        marks: {
          kind: 'grille',
          rows: Math.round(h / 0.16),
          cols: 1,
          c: '#A9B0B8',
          front: true,
          z0: 0.08,
          z1: 0.75,
        },
      });
      pieces.push({
        s: 'box',
        x: x + dx + 0.01,
        y: y + 0.16,
        w: 0.22,
        d: 0.66,
        z: 0.03 + h * 0.8,
        h: h * 0.12,
        c: '#D7DBE0',
        marks: {
          kind: 'led',
          rows: 1,
          cols: 2,
          c: '#2E3E52',
          on: sweepOn(p) ? C.cyan : C.blue,
          lit: litShare(p),
          blink: true,
          busy: busyShare(p),
          front: true,
        },
      });
    }
    return {
      x,
      y,
      w: 1,
      d: 1,
      id: p.id,
      fine: cage(x + 0.05, y + 0.09, 0.9, 0.82, h + 0.12),
      pieces,
    };
  });
};

/** D382: a hall's roof: where it is and how high, once its storeys are stepped. */
interface HallTop {
  x: number;
  y: number;
  w: number;
  d: number;
  /** The roof's height, and the podium's under a set-back storey (the same when none). */
  h: number;
  h0: number;
}

/**
 * Server rooms: a building over the plot, a storey more every other milestone. D382: it stands
 * on a plinth; past two storeys its top third steps back from the two faces the eye sees; a
 * glazed lobby with a canopy stands proud of its front, a lit fascia over the doors; its roof
 * carries a parapet with a capping, a walkway and rooflights round the plant.
 */
function hall(
  wall: string,
  roof: string,
  per: number,
  cap: number,
  plant: number,
  extra?: (r: Bounds, h: number, p: IsoParcel, top: HallTop) => Piece[],
  face: Facade = 'band',
): Model {
  return (p) => {
    const tier = p.tier ?? 0;
    const r = mainRect(p.tiles);
    const floors = 1 + steps(tier, per, cap);
    const out: Unit[] = [];
    if (r.w > 0) {
      const i = inset(r.x, r.y, r.w, r.h, 0.08);
      const big = i.w >= 1.3 && i.d >= 1.3;
      const up = floors >= 3 && big ? Math.ceil(floors / 3) : 0;
      const low = floors - up;
      const louvred = face === 'louvre';
      const marks = (rows: number, w: number, d: number): FaceMarks =>
        louvred
          ? {
              kind: 'grille',
              rows,
              cols: Math.max(2, Math.round(Math.max(w, d) * 2.5)),
              c: C.louvre,
              z0: 0.12,
              z1: 0.9,
            }
          : {
              kind: 'win',
              rows,
              cols: Math.max(1, Math.round(Math.max(w, d) * 3)),
              c: C.window,
              on: C.lit,
              z0: 0.06,
              z1: 0.96,
            };
      const h0 = low * FLOOR + 0.08;
      const pieces: Piece[] = [
        plinth(i.x, i.y, i.w, i.d),
        {
          s: 'box',
          x: i.x,
          y: i.y,
          w: i.w,
          d: i.d,
          z: 0,
          h: h0,
          c: wall,
          top: roof,
          roof: true,
          face,
          marks: marks(low, i.w, i.d),
        },
      ];
      const fine: Piece[] = [];
      let top: HallTop = { x: i.x, y: i.y, w: i.w, d: i.d, h: h0, h0 };
      if (up) {
        const t = { x: i.x + 0.05, y: i.y + 0.05, w: i.w - 0.24, d: i.d - 0.24 };
        const hu = up * FLOOR + 0.04;
        pieces.push({
          s: 'box',
          ...t,
          z: h0,
          h: hu,
          c: wall,
          top: roof,
          roof: true,
          face,
          marks: marks(up, t.w, t.d),
        });
        top = { ...t, h: h0 + hu, h0 };
        fine.push(...parapet(i.x, i.y, i.w, i.d, h0, wall));
      }
      const h = top.h;
      pieces.push(...doors(i.x, i.y, i.w, i.d));
      // The lobby, glazed, its canopy over the way in; the fascia over the doors.
      if (i.w >= 1.4) {
        const lw = Math.min(0.62, i.w * 0.28),
          lx = i.x + i.w * 0.1;
        pieces.push(
          {
            s: 'box',
            x: lx,
            y: i.y + i.d - 0.16,
            w: lw,
            d: 0.3,
            z: 0,
            h: FLOOR + 0.08,
            c: C.lobby,
            top: C.light,
            roof: true,
            face: 'curtain',
            marks: { kind: 'win', rows: 1, cols: 3, c: C.window, on: C.lit, z0: 0.08, z1: 0.9 },
          },
          {
            s: 'box',
            x: lx - 0.05,
            y: i.y + i.d - 0.16,
            w: lw + 0.1,
            d: 0.38,
            z: FLOOR + 0.08,
            h: 0.035,
            c: C.white,
            roof: true,
          },
        );
      }
      if (h0 >= 0.36)
        pieces.push({
          s: 'box',
          x: i.x + i.w * 0.5,
          y: i.y + i.d - 0.002,
          w: Math.min(0.8, i.w * 0.36),
          d: 0.022,
          z: h0 - 0.13,
          h: 0.08,
          c: C.sign,
          face: 'sign',
        });
      pieces.push(
        ...roofPlant(
          top.x + 0.1,
          top.y + 0.1,
          top.w - 0.2,
          top.d - 0.36,
          h,
          // D382: a big roof carries its condensers in full rows.
          Math.min(
            24,
            Math.max(
              Math.min(8, plant + Math.floor(tier / 3)),
              Math.floor((top.w - 0.2) / 0.38) * Math.max(1, Math.floor((top.d - 0.9) / 0.42)),
            ),
          ),
        ),
      );
      if (extra) pieces.push(...extra(r, h, p, top));
      fine.push(...parapet(top.x, top.y, top.w, top.d, h, wall));
      fine.push(...roofDetail(top.x, top.y, top.w, top.d, h));
      out.push({ x: r.x, y: r.y, w: r.w, d: r.h, pieces, fine, id: p.id });
    }
    // The rest of the plot: chillers on pads, their fans turning; D381: a standby generator
    // on about one tile in three.
    const inMain = (x: number, y: number) => x >= r.x && x < r.x + r.w && y >= r.y && y < r.y + r.h;
    for (const [x, y] of tilesOf(p)) {
      if (inMain(x, y)) continue;
      if (hashXY(x, y, 5) < 0.34) {
        out.push({ x, y, w: 1, d: 1, id: p.id, pieces: generator(x, y) });
        continue;
      }
      out.push({
        x,
        y,
        w: 1,
        d: 1,
        id: p.id,
        pieces: [
          { s: 'box', x: x + 0.07, y: y + 0.13, w: 0.86, d: 0.74, z: 0, h: 0.035, c: C.pad },
          {
            s: 'box',
            x: x + 0.12,
            y: y + 0.18,
            w: 0.76,
            d: 0.64,
            z: 0,
            h: 0.24,
            c: C.light,
            top: C.mid,
            roof: true,
            face: 'louvre',
            marks: { kind: 'grille', rows: 2, cols: 3, c: C.mid },
          },
          { s: 'disc', x: x + 0.32, y: y + 0.5, r: 0.14, z: 0.245, c: C.dark, fan: true },
          { s: 'disc', x: x + 0.68, y: y + 0.5, r: 0.14, z: 0.245, c: C.dark, fan: true },
        ],
      });
    }
    return out;
  };
}

/** GPU nodes: dark boxes with a green glow down their fronts, stacked as the plot grows. */
const gpuNodes: Model = (p) => {
  const tier = p.tier ?? 0;
  const stack = 1 + steps(tier, 3, 4);
  return tilesOf(p).map(([x, y]) => {
    // D382: liquid-cooled pods on a raised floor, the cold and warm manifolds over them.
    const pieces: Piece[] = [
      { s: 'box', x: x + 0.05, y: y + 0.07, w: 0.9, d: 0.86, z: 0, h: 0.04, c: C.floor },
    ];
    for (let s = 0; s < stack; s++) {
      const z = 0.04 + s * 0.3;
      pieces.push({
        s: 'box',
        x: x + 0.1,
        y: y + 0.12,
        w: 0.8,
        d: 0.76,
        z,
        h: 0.28,
        c: C.black,
        top: '#30343A',
        roof: s === stack - 1,
        face: 'rack',
        marks: {
          kind: 'band',
          rows: 2,
          cols: 1,
          c: '#2A4A38',
          on: sweepOn(p) ? C.cyan : C.glow,
          lit: litShare(p),
          front: true,
          z0: 0.25,
          z1: 0.8,
        },
      });
    }
    const zt = 0.04 + stack * 0.3 + 0.03;
    [C.pipeCold, C.pipeHot].forEach((c, k) =>
      pieces.push({
        s: 'rod',
        a: [x + 0.04, y + 0.3 + k * 0.2, zt],
        b: [x + 0.96, y + 0.3 + k * 0.2, zt],
        t: 0.04,
        c,
      }),
    );
    return { x, y, w: 1, d: 1, pieces, id: p.id };
  });
};

/** Cooling towers: waisted towers, one to a tile, rising with the milestones; vapour above. */
const coolingTowers: Model = (p) => {
  const tier = p.tier ?? 0;
  const h = 0.8 + Math.min(1.8, tier * 0.12);
  return tilesOf(p).map(([x, y]) => ({
    x,
    y,
    w: 1,
    d: 1,
    id: p.id,
    pieces: [
      { s: 'cyl', x: x + 0.5, y: y + 0.5, r: 0.46, z: 0, h: 0.06, c: C.pad },
      { s: 'tower', x: x + 0.5, y: y + 0.5, r: 0.42, z: 0.04, h, c: '#D3D0C8' },
    ],
  }));
};

/** The height a cooling tower reaches, for its vapour. */
export function coolingTop(p: IsoParcel): number {
  return 0.84 + Math.min(1.8, (p.tier ?? 0) * 0.12);
}

/** Substations: transformers behind a fence, gantries and lines, pylons rising with the tiers. */
const substations: Model = (p) => {
  const tier = p.tier ?? 0;
  const mast = 0.7 + steps(tier, 2, 6) * 0.18;
  const own = new Set(p.tiles);
  return tilesOf(p).map(([x, y], i) => {
    const pieces: Piece[] = [];
    // D381: a palisade along the plot's outer edges, and porcelain on every live part.
    const fine: Piece[] = [];
    const sides: Array<[number, number, number, number, string]> = [
      [x, y, x + 1, y, `${x},${y - 1}`],
      [x, y + 1, x + 1, y + 1, `${x},${y + 1}`],
      [x, y, x, y + 1, `${x - 1},${y}`],
      [x + 1, y, x + 1, y + 1, `${x + 1},${y}`],
    ];
    for (const [ax, ay, bx, by, next] of sides) {
      if (own.has(next)) continue;
      for (let k = 0; k <= 4; k++) {
        const px = ax + ((bx - ax) * k) / 4,
          py = ay + ((by - ay) * k) / 4;
        fine.push({ s: 'rod', a: [px, py, 0], b: [px, py, 0.3], t: 0.02, c: C.palisade });
      }
      for (const z of [0.08, 0.26])
        fine.push({ s: 'rod', a: [ax, ay, z], b: [bx, by, z], t: 0.018, c: C.palisade });
    }
    // D382: each bay on its own bed of gravel, kerbed.
    pieces.push({ s: 'box', x: x + 0.05, y: y + 0.05, w: 0.9, d: 0.9, z: 0, h: 0.02, c: C.gravel });
    if (i % 2 === 0) {
      // A transformer: a body with cooling fins and three bushings, on its plinth.
      pieces.push({ s: 'box', x: x + 0.17, y: y + 0.2, w: 0.66, d: 0.6, z: 0, h: 0.04, c: C.pad });
      pieces.push({
        s: 'box',
        x: x + 0.22,
        y: y + 0.25,
        w: 0.56,
        d: 0.5,
        z: 0,
        h: 0.34,
        c: '#7D8A78',
        top: '#8E9B88',
        roof: true,
        face: 'ribbed',
        marks: { kind: 'grille', rows: 1, cols: 6, c: '#66725F', z0: 0.15, z1: 0.85 },
      });
      for (const dx of [0.32, 0.5, 0.68]) {
        pieces.push({
          s: 'cyl',
          x: x + dx,
          y: y + 0.5,
          r: 0.035,
          z: 0.34,
          h: 0.16,
          c: C.insulator,
        });
        for (const dz of [0.05, 0.09, 0.13])
          fine.push({
            s: 'cyl',
            x: x + dx,
            y: y + 0.5,
            r: 0.05,
            z: 0.34 + dz,
            h: 0.015,
            c: C.insulator,
          });
      }
      // The conservator tank on its stand, at the back of the transformer's lid.
      fine.push({
        s: 'box',
        x: x + 0.28,
        y: y + 0.27,
        w: 0.44,
        d: 0.08,
        z: 0.4,
        h: 0.08,
        c: '#7D8A78',
      });
      fine.push({
        s: 'rod',
        a: [x + 0.34, y + 0.31, 0.34],
        b: [x + 0.34, y + 0.31, 0.4],
        t: 0.02,
        c: C.steel,
      });
      fine.push({
        s: 'rod',
        a: [x + 0.66, y + 0.31, 0.34],
        b: [x + 0.66, y + 0.31, 0.4],
        t: 0.02,
        c: C.steel,
      });
    } else {
      // A gantry: two lattice legs and a beam, the lines running off it.
      for (const dx of [0.2, 0.8])
        pieces.push({
          s: 'rod',
          a: [x + dx, y + 0.5, 0],
          b: [x + dx, y + 0.5, mast],
          t: 0.05,
          c: C.steel,
        });
      pieces.push({
        s: 'rod',
        a: [x + 0.12, y + 0.5, mast],
        b: [x + 0.88, y + 0.5, mast],
        t: 0.045,
        c: C.steel,
      });
      for (const dx of [0.3, 0.5, 0.7]) {
        pieces.push({
          s: 'rod',
          a: [x + dx, y + 0.5, mast - 0.02],
          b: [x + dx, y - 0.4, mast * 0.7],
          t: 0.012,
          c: '#3A3D42',
        });
        // A string of insulators hung under the beam where each line meets it.
        fine.push({
          s: 'cyl',
          x: x + dx,
          y: y + 0.5,
          r: 0.025,
          z: mast - 0.13,
          h: 0.11,
          c: C.insulator,
        });
      }
    }
    return { x, y, w: 1, d: 1, pieces, fine, id: p.id };
  });
};

/** Interconnects: a windowless meet-me tower, coloured cable risers up its side. */
const interconnects = hall(C.light, C.roof, 2, 7, 2, (_r, h, _p, t) => {
  const out: Piece[] = [];
  [C.blue, C.yellow, C.red].forEach((c, i) =>
    out.push({
      s: 'box',
      x: t.x + t.w - 0.04,
      y: t.y + 0.12 + i * 0.12,
      w: 0.05,
      d: 0.06,
      z: 0,
      h: h - 0.04,
      c,
    }),
  );
  out.push({
    s: 'rod',
    a: [t.x + t.w * 0.5, t.y + t.d * 0.4, h],
    b: [t.x + t.w * 0.5, t.y + t.d * 0.4, h + 0.6],
    t: 0.04,
    c: C.steel,
  });
  return out;
});

/**
 * Data halls: long louvred halls with fans on the roof, taller with the milestones. D381: a
 * glazed office end, a storey high, proud of the hall's east end, lit through the night.
 */
const dataHalls = hall(
  '#DADDE2',
  '#A9AFB6',
  3,
  5,
  4,
  (r, h) => {
    const ow = Math.min(0.55, r.w * 0.3);
    const stripe: Piece = {
      s: 'box',
      x: r.x + 0.08,
      y: r.y + r.h * 0.45,
      w: r.w - 0.16,
      d: 0.05,
      z: h * 0.1,
      h: h * 0.08,
      c: C.blue,
      glow: false,
    };
    if (r.w < 2) return [stripe];
    return [
      stripe,
      {
        s: 'box',
        x: r.x + r.w - 0.08 - ow,
        y: r.y + 0.05,
        w: ow + 0.03,
        d: r.h - 0.07,
        z: 0,
        h: (h > 0.8 ? 2 : 1) * FLOOR + 0.06,
        c: '#B9CCDA',
        top: '#B9BEC4',
        roof: true,
        face: 'curtain',
        marks: {
          kind: 'win',
          rows: h > 0.8 ? 2 : 1,
          cols: 3,
          c: C.window,
          on: C.lit,
          z0: 0.2,
          z1: 0.85,
        },
      },
    ];
  },
  'louvre',
);

/** Edge sites: green street cabinets and a mast, the mast taller as it grows. */
const edgeSites: Model = (p) => {
  const tier = p.tier ?? 0;
  const mast = 0.9 + steps(tier, 2, 6) * 0.2;
  return tilesOf(p).map(([x, y], i) => {
    const pieces: Piece[] = [
      {
        s: 'box',
        x: x + 0.15,
        y: y + 0.3,
        w: 0.36,
        d: 0.3,
        z: 0,
        h: 0.36,
        c: '#4F7A5C',
        top: '#5E8A6A',
        roof: true,
        face: 'clad',
        marks: { kind: 'grille', rows: 2, cols: 2, c: '#406A4C', front: true },
      },
      {
        s: 'box',
        x: x + 0.55,
        y: y + 0.3,
        w: 0.3,
        d: 0.3,
        z: 0,
        h: 0.3,
        c: '#5A6068',
        marks: {
          kind: 'led',
          rows: 1,
          cols: 3,
          c: C.rackLed,
          on: C.green,
          lit: litShare(p),
          blink: true,
          busy: busyShare(p),
          front: true,
        },
      },
    ];
    if (i % 2 === 0) {
      pieces.push({
        s: 'rod',
        a: [x + 0.5, y + 0.75, 0],
        b: [x + 0.5, y + 0.75, mast],
        t: 0.05,
        c: C.steel,
      });
      for (const a of [0, 2.1, 4.2])
        pieces.push({
          s: 'box',
          x: x + 0.5 + Math.cos(a) * 0.06 - 0.03,
          y: y + 0.75 + Math.sin(a) * 0.06 - 0.03,
          w: 0.06,
          d: 0.06,
          z: mast - 0.25,
          h: 0.2,
          c: '#E4E7EA',
        });
    }
    return { x, y, w: 1, d: 1, pieces, id: p.id };
  });
};

/** Solar arrays: rows of tilted panels, raised on taller frames as the milestones mount. */
const solarArrays: Model = (p) => {
  const tier = p.tier ?? 0;
  const lift = 0.06 + steps(tier, 3, 5) * 0.12;
  return tilesOf(p).map(([x, y]) => {
    const pieces: Piece[] = [];
    for (const dy of [0.08, 0.55]) {
      for (const lx of [0.15, 0.85])
        pieces.push({
          s: 'box',
          x: x + lx - 0.02,
          y: y + dy + 0.18,
          w: 0.04,
          d: 0.04,
          z: 0,
          h: lift + 0.05,
          c: C.steel,
        });
      pieces.push({
        s: 'panel',
        x: x + 0.06,
        y: y + dy,
        w: 0.88,
        d: 0.36,
        z: lift,
        rise: 0.18,
        c: C.solar,
      });
    }
    return { x, y, w: 1, d: 1, pieces, id: p.id };
  });
};

/** Subsea cables: a landing station, cable drums stacked higher as it grows, the cable's duct. */
const subsea: Model = (p) => {
  const tier = p.tier ?? 0;
  const drums = 1 + steps(tier, 3, 4);
  return tilesOf(p).map(([x, y], i) => {
    const pieces: Piece[] = [];
    if (i === 0) {
      pieces.push(...block(x + 0.1, y + 0.1, 0.8, 0.8, 1 + steps(tier, 4, 3), '#CFC9BC', C.roof));
    } else {
      for (let d = 0; d < drums; d++) {
        const z = d * 0.2;
        pieces.push({ s: 'cyl', x: x + 0.5, y: y + 0.5, r: 0.34, z, h: 0.04, c: C.drum });
        pieces.push({
          s: 'cyl',
          x: x + 0.5,
          y: y + 0.5,
          r: 0.24,
          z: z + 0.04,
          h: 0.12,
          c: '#2E3136',
        });
        pieces.push({ s: 'cyl', x: x + 0.5, y: y + 0.5, r: 0.34, z: z + 0.16, h: 0.04, c: C.drum });
      }
    }
    pieces.push({ s: 'box', x: x + 0.44, y, w: 0.12, d: 1, z: 0, h: 0.03, c: '#2E3136' });
    return { x, y, w: 1, d: 1, pieces, id: p.id };
  });
};

/** Custom chips: a white clean-room fab, its exhaust stacks taller as it grows. */
const customChips = hall('#F1F2F3', '#D6D9DD', 2, 6, 2, (r, h, p) => {
  const out: Piece[] = [];
  const stack = h + 0.4 + (p.tier ?? 0) * 0.04;
  for (const f of [0.3, 0.7])
    out.push({
      s: 'cyl',
      x: r.x + r.w * f,
      y: r.y + 0.25,
      r: 0.07,
      z: 0,
      h: stack,
      c: '#BFC4C9',
      top: '#8E949C',
    });
  return out;
});

/** Superclusters: huge black halls banded in blue light, rising tall with the milestones. */
const superclusters = hall('#26292F', '#3A3E45', 2, 9, 6, (r, h, p, t) => [
  {
    s: 'box',
    x: r.x + 0.07,
    y: r.y + 0.07,
    w: r.w - 0.14,
    d: r.h - 0.14,
    z: t.h0 * (t.h0 < h ? 0.5 : 0.3),
    h: 0.05,
    c: '#2A3A55',
    glow: true,
    marks: {
      kind: 'band',
      rows: 1,
      cols: 1,
      c: C.blue,
      on: sweepOn(p) ? C.cyan : C.blue,
    },
  },
  {
    s: 'box',
    x: t.h0 < h ? t.x - 0.01 : r.x + 0.07,
    y: t.h0 < h ? t.y - 0.01 : r.y + 0.07,
    w: t.h0 < h ? t.w + 0.02 : r.w - 0.14,
    d: t.h0 < h ? t.d + 0.02 : r.h - 0.14,
    z: t.h0 < h ? (t.h0 + h) / 2 : h * 0.7,
    h: 0.05,
    c: '#2A3A55',
    glow: true,
    marks: { kind: 'band', rows: 1, cols: 1, c: C.blue, on: C.blue },
  },
]);

/** Photonic chips: a glass-fronted lab, a red beam across its roof. */
const photonic = hall(
  C.glass,
  '#C7CCD2',
  2,
  5,
  2,
  (_r, h, _p, t) => [
    {
      s: 'rod',
      a: [t.x + 0.15, t.y + t.d * 0.5, h + 0.12],
      b: [t.x + t.w - 0.15, t.y + t.d * 0.5, h + 0.12],
      t: 0.025,
      c: C.red,
      glow: true,
    },
    { s: 'cyl', x: t.x + 0.15, y: t.y + t.d * 0.5, r: 0.05, z: h, h: 0.14, c: C.dark },
  ],
  'curtain',
);

/** Quantum computers: a white lab with a dome, a glowing gold cryostat beside it. */
const quantum = hall(C.white, '#D6D9DD', 2, 5, 1, (_r, h, _p, t) => {
  const r = { w: t.w, h: t.d };
  const cx = t.x + t.w / 2,
    cy = t.y + t.d / 2;
  return [
    {
      s: 'cyl',
      x: cx,
      y: cy,
      r: Math.min(r.w, r.h) * 0.3,
      z: h,
      h: 0.25,
      c: '#E4E7EA',
      top: '#F4F5F6',
    },
    {
      s: 'cyl',
      x: cx,
      y: cy,
      r: Math.min(r.w, r.h) * 0.16,
      z: h + 0.25,
      h: 0.12,
      c: C.cyan,
      glow: true,
    },
  ];
});

/** Each producer's own look, by id. A producer with none takes the plain hall. */
export const MODELS: Record<string, Model> = {
  laptops,
  home_servers: homeServers,
  racks,
  switches,
  fibre_links: fibreLinks,
  storage_arrays: storage,
  server_rooms: hall(C.light, C.roof, 2, 7, 3),
  gpu_nodes: gpuNodes,
  cooling_towers: coolingTowers,
  substations,
  interconnects,
  data_halls: dataHalls,
  edge_sites: edgeSites,
  solar_arrays: solarArrays,
  subsea_cables: subsea,
  custom_chips: customChips,
  superclusters,
  photonic_chips: photonic,
  quantum_computers: quantum,
};

const plainHall = hall(C.light, C.roof, 2, 6, 2);

/** A producer's plot as units. Empty for land with no producer on it. */
export function plotUnits(p: IsoParcel, L: Look): Unit[] {
  if (!p.id || p.t !== 'field' || !p.tiles.length) return [];
  return (MODELS[p.id] ?? plainHall)(p, L);
}

/* ---------- the yard, the chains and the marks ---------- */

/** D291: the building decoration's walls and roof, from `look` (`building+roof`). */
function decorOf(look?: string): { wall?: string; roof?: string } {
  const [b, r] = (look ?? '').split('+');
  return {
    ...(CLADDING[b ?? ''] ? { wall: CLADDING[b ?? ''] } : {}),
    ...(ROOFING[r ?? ''] ? { roof: ROOFING[r ?? ''] } : {}),
  };
}

/** How many storeys a building's producer's milestones give it (D268's `style`). */
function styleFloors(style?: string): number {
  return style === 'stone' ? 3 : style === 'timber' ? 2 : 1;
}

/**
 * The yard's two buildings and each chain's building. The house is the founders' office (a
 * house with its garage, where it all began); the barn the warehouse, wider with its level.
 * A chain's building has its own silhouette by what it is, taller as its producer grows.
 */
export function buildingUnits(b: IsoBuildingAt): Unit[] {
  const dec = decorOf(b.look);
  const { x, y, w, h } = b;
  const i = inset(x, y, w, h, 0.08);
  const pieces: Piece[] = [];
  const wall = dec.wall,
    roof = dec.roof;
  const unit = (ps: Piece[]): Unit[] => {
    // D382: the tallest faced wall's flat roof takes a capped parapet.
    let main: Extract<Piece, { s: 'box' }> | null = null;
    for (const q of ps)
      if (q.s === 'box' && q.face && /band|curtain|punched|clad|louvre/.test(q.face) && q.roof)
        if (!main || q.z + q.h > main.z + main.h) main = q;
    const fine = main ? parapet(main.x, main.y, main.w, main.d, main.z + main.h, main.c) : [];
    return [{ x, y, w, d: h, pieces: ps, fine, ...(b.id ? { id: b.id } : {}) }];
  };
  if (b.kind === 'house') {
    pieces.push(
      ...block(
        i.x,
        i.y,
        i.w * 0.6,
        i.d,
        2,
        wall ?? '#E7DCC8',
        roof ?? '#7A5A48',
        C.window,
        C.lit,
        0,
        'punched',
      ),
    );
    pieces.push({
      s: 'box',
      x: i.x + i.w * 0.6,
      y: i.y + 0.2,
      w: i.w * 0.4,
      d: i.d - 0.2,
      z: 0,
      h: 0.42,
      c: wall ?? '#D8CFBF',
      top: roof ?? C.roof,
      roof: true,
      face: 'clad',
    });
    pieces.push({
      s: 'box',
      x: i.x + i.w * 0.66,
      y: i.y + i.d - 0.02,
      w: i.w * 0.28,
      d: 0.03,
      z: 0,
      h: 0.32,
      c: '#B9BCC0',
      face: 'shutter',
      marks: { kind: 'grille', rows: 5, cols: 1, c: '#9DA1A6' },
    });
    return unit(pieces);
  }
  if (b.kind === 'barn') {
    pieces.push({
      s: 'box',
      x: i.x,
      y: i.y,
      w: i.w,
      d: i.d,
      z: 0,
      h: 0.62,
      c: wall ?? '#B9C0C8',
      top: roof ?? '#8C939B',
      roof: true,
      face: 'ribbed',
    });
    pieces.unshift(plinth(i.x, i.y, i.w, i.d));
    pieces.push({ s: 'box', x: i.x, y: i.y + i.d, w: i.w, d: 0.12, z: 0.62, h: 0.03, c: C.dark });
    // D382: loading bays along its front, each a roller shutter over a dock.
    const bays = Math.max(2, Math.round(i.w * 1.5));
    for (let k = 0; k < bays; k++) {
      const bx = i.x + ((k + 0.5) * i.w) / bays - 0.15;
      pieces.push(
        { s: 'box', x: bx - 0.02, y: i.y + i.d - 0.01, w: 0.34, d: 0.08, z: 0, h: 0.1, c: C.pad },
        {
          s: 'box',
          x: bx,
          y: i.y + i.d - 0.005,
          w: 0.3,
          d: 0.025,
          z: 0.1,
          h: 0.36,
          c: '#A9B0B8',
          face: 'shutter',
        },
      );
    }
    return unit(pieces);
  }
  const floors = styleFloors(b.style);
  const c = b.chain ?? '';
  const W = wall ?? C.white,
    R = roof ?? C.roof;
  if (/desk|office/.test(c)) {
    // A glass office, its storeys from its producer's milestones.
    pieces.push(
      ...block(
        i.x,
        i.y,
        i.w,
        i.d,
        floors + 1,
        wall ?? C.glass,
        roof ?? '#C7CCD2',
        '#5C83A3',
        C.lit,
        0,
        'curtain',
      ),
    );
  } else if (/studio/.test(c)) {
    // A brick studio with a saw-tooth roof of skylights.
    pieces.push(
      ...block(
        i.x,
        i.y,
        i.w,
        i.d,
        floors,
        wall ?? C.brick,
        roof ?? C.roof,
        C.window,
        C.lit,
        0,
        'punched',
      ),
    );
    const top = floors * FLOOR + 0.08;
    for (let k = 0; k < Math.max(1, Math.round(i.w * 3)); k++)
      pieces.push({
        s: 'panel',
        x: i.x + (k * i.w) / Math.max(1, Math.round(i.w * 3)),
        y: i.y + 0.05,
        w: i.w / Math.max(1, Math.round(i.w * 3)),
        d: i.d - 0.1,
        z: top,
        rise: 0.14,
        c: C.glassTop,
      });
  } else if (/exchange/.test(c) && c !== 'heat_exchange') {
    // A windowless exchange with dishes on the roof.
    pieces.push({
      s: 'box',
      x: i.x,
      y: i.y,
      w: i.w,
      d: i.d,
      z: 0,
      h: floors * FLOOR + 0.2,
      c: wall ?? '#CDD1D6',
      top: R,
      roof: true,
      face: 'louvre',
      marks: { kind: 'grille', rows: floors, cols: 3, c: '#AEB4BA' },
    });
    const top = floors * FLOOR + 0.2;
    pieces.push({
      s: 'cyl',
      x: i.x + i.w * 0.3,
      y: i.y + i.d * 0.4,
      r: 0.04,
      z: top,
      h: 0.2,
      c: C.steel,
    });
    pieces.push({
      s: 'disc',
      x: i.x + i.w * 0.3,
      y: i.y + i.d * 0.4,
      r: 0.18,
      z: top + 0.2,
      c: '#EEF0F2',
    });
  } else if (c === 'heat_exchange') {
    pieces.push({
      s: 'box',
      x: i.x,
      y: i.y,
      w: i.w,
      d: i.d,
      z: 0,
      h: 0.3 + floors * 0.1,
      c: C.light,
      top: C.mid,
      roof: true,
      face: 'louvre',
      marks: { kind: 'grille', rows: 2, cols: 4, c: C.mid },
    });
    pieces.push(...roofPlant(i.x, i.y, i.w, i.d, 0.3 + floors * 0.1, 2 + floors));
    pieces.push({
      s: 'rod',
      a: [i.x + i.w, i.y + i.d * 0.3, 0.2],
      b: [i.x + i.w + 0.06, i.y + i.d * 0.3, 0.02],
      t: 0.05,
      c: C.copper,
    });
  } else if (/store|vault/.test(c)) {
    // Battery containers, or the vault's squat bunker.
    if (c === 'backup_vault') {
      pieces.push({
        s: 'box',
        x: i.x,
        y: i.y,
        w: i.w,
        d: i.d,
        z: 0,
        h: 0.3 + floors * 0.08,
        c: wall ?? '#9EA19C',
        top: '#8A8D88',
        roof: true,
        face: 'clad',
      });
      pieces.push({
        s: 'cyl',
        x: i.x + i.w / 2,
        y: i.y + i.d,
        r: 0.12,
        z: 0.06,
        h: 0.02,
        c: C.steel,
      });
    } else
      for (let k = 0; k < floors + 1; k++)
        pieces.push({
          s: 'box',
          x: i.x,
          y: i.y + (k * i.d) / (floors + 1),
          w: i.w,
          d: i.d / (floors + 1) - 0.04,
          z: 0,
          h: 0.3,
          c: wall ?? '#E4E7EA',
          top: '#C9CED3',
          roof: true,
          face: 'ribbed',
          marks: {
            kind: 'led',
            rows: 1,
            cols: 2,
            c: C.rackLed,
            on: C.green,
            lit: 1,
            front: true,
            z0: 0.6,
            z1: 0.8,
          },
        });
  } else if (c === 'landing_station') {
    pieces.push(...block(i.x, i.y, i.w, i.d, floors, wall ?? '#CFC9BC', R));
    pieces.push({
      s: 'cyl',
      x: i.x + i.w * 0.75,
      y: i.y + i.d * 0.75,
      r: 0.12,
      z: floors * FLOOR + 0.08,
      h: 0.1,
      c: C.drum,
    });
  } else if (c === 'virtualisation' || c === 'container_platform') {
    // Shipping containers stacked: the platform in boxes.
    const colours =
      c === 'virtualisation' ? [C.blue, '#3E7A6A', C.violet] : [C.drum, C.blue, '#3E7A46', C.red];
    for (let k = 0; k < floors + 1; k++)
      pieces.push({
        s: 'box',
        x: i.x + (k % 2) * 0.04,
        y: i.y,
        w: i.w - 0.04,
        d: i.d,
        z: k * 0.22,
        h: 0.2,
        c: colours[k % colours.length] as string,
        roof: k === floors,
        face: 'ribbed',
        marks: { kind: 'grille', rows: 1, cols: 5, c: '#2A2D33' },
      });
  } else if (/lab/.test(c)) {
    // A lab: white, a dome or skylight on top.
    pieces.push(...block(i.x, i.y, i.w, i.d, floors, W, R, '#5C83A3'));
    const top = floors * FLOOR + 0.08;
    pieces.push({
      s: 'cyl',
      x: i.x + i.w / 2,
      y: i.y + i.d / 2,
      r: Math.min(i.w, i.d) * 0.3,
      z: top,
      h: 0.14,
      c: '#E4E7EA',
      top: c === 'frontier_lab' ? C.violet : '#F4F5F6',
    });
  } else if (c === 'inference_hall') {
    pieces.push(
      ...block(i.x, i.y, i.w, i.d, floors, wall ?? '#2E3238', roof ?? '#3A3E45', '#3A5A7A', C.cyan),
    );
    pieces.push({
      s: 'box',
      x: i.x,
      y: i.y + i.d * 0.5,
      w: i.w,
      d: 0.04,
      z: 0.1,
      h: 0.05,
      c: C.cyan,
      glow: true,
    });
  } else if (c === 'content_cache') {
    for (const dx of [0, 0.5])
      pieces.push({
        s: 'box',
        x: i.x + dx * i.w,
        y: i.y,
        w: i.w * 0.45,
        d: i.d,
        z: 0,
        h: 0.32 + floors * 0.08,
        c: '#4F7A5C',
        top: '#5E8A6A',
        roof: true,
        face: 'rack',
        marks: {
          kind: 'led',
          rows: 2,
          cols: 2,
          c: C.rackLed,
          on: C.green,
          lit: 1,
          blink: true,
          front: true,
        },
      });
  } else pieces.push(...block(i.x, i.y, i.w, i.d, floors, W, R));
  return unit(pieces);
}

/** The flat map's festival mark footprint half-size (as `MARK_HALF`). */
const MH = 0.7;

/** D245: a festival's lasting mark, each its own small landmark. */
export function markUnits(kind: string, x: number, y: number): Unit[] {
  const pieces: Piece[] = [];
  switch (kind) {
    case 'founders_plaque':
      pieces.push({
        s: 'box',
        x: x - 0.25,
        y: y - 0.15,
        w: 0.5,
        d: 0.3,
        z: 0,
        h: 0.2,
        c: '#9EA19C',
        roof: true,
      });
      pieces.push({
        s: 'panel',
        x: x - 0.2,
        y: y - 0.1,
        w: 0.4,
        d: 0.2,
        z: 0.2,
        rise: 0.12,
        c: C.copper,
      });
      break;
    case 'radio_mast':
      for (const [dx, dy] of [
        [-0.15, -0.15],
        [0.15, -0.15],
        [0, 0.15],
      ] as const)
        pieces.push({ s: 'rod', a: [x + dx, y + dy, 0], b: [x, y, 1.9], t: 0.03, c: '#C4382E' });
      pieces.push({ s: 'cyl', x, y, r: 0.04, z: 1.9, h: 0.06, c: C.red, glow: true });
      break;
    case 'solar_canopy':
      for (const [dx, dy] of [
        [-0.4, -0.3],
        [0.4, -0.3],
        [-0.4, 0.3],
        [0.4, 0.3],
      ] as const)
        pieces.push({
          s: 'box',
          x: x + dx - 0.02,
          y: y + dy - 0.02,
          w: 0.04,
          d: 0.04,
          z: 0,
          h: 0.5,
          c: C.steel,
        });
      pieces.push({
        s: 'panel',
        x: x - 0.5,
        y: y - 0.4,
        w: 1,
        d: 0.8,
        z: 0.5,
        rise: 0.15,
        c: C.solar,
      });
      break;
    case 'wind_turbine':
      pieces.push({ s: 'cyl', x, y, r: 0.06, z: 0, h: 1.8, c: '#EEF0F2' });
      pieces.push({
        s: 'box',
        x: x - 0.08,
        y: y - 0.06,
        w: 0.2,
        d: 0.12,
        z: 1.78,
        h: 0.1,
        c: '#EEF0F2',
      });
      for (const a of [Math.PI / 2, Math.PI / 2 + 2.09, Math.PI / 2 + 4.19])
        pieces.push({
          s: 'rod',
          a: [x + 0.13, y, 1.83],
          b: [x + 0.13, y + Math.cos(a) * 0.65, 1.83 + Math.sin(a) * 0.65],
          t: 0.04,
          c: '#F4F5F6',
        });
      break;
    case 'satellite_dish':
      pieces.push({
        s: 'box',
        x: x - 0.2,
        y: y - 0.2,
        w: 0.4,
        d: 0.4,
        z: 0,
        h: 0.15,
        c: '#9EA19C',
        roof: true,
      });
      pieces.push({ s: 'cyl', x, y, r: 0.05, z: 0.15, h: 0.35, c: C.steel });
      pieces.push({
        s: 'panel',
        x: x - 0.4,
        y: y - 0.25,
        w: 0.8,
        d: 0.5,
        z: 0.45,
        rise: 0.45,
        c: '#EEF0F2',
      });
      break;
    case 'water_tank':
      for (const [dx, dy] of [
        [-0.25, -0.25],
        [0.25, -0.25],
        [-0.25, 0.25],
        [0.25, 0.25],
      ] as const)
        pieces.push({
          s: 'rod',
          a: [x + dx, y + dy, 0],
          b: [x + dx * 0.8, y + dy * 0.8, 0.7],
          t: 0.04,
          c: C.steel,
        });
      pieces.push({ s: 'cyl', x, y, r: 0.36, z: 0.7, h: 0.5, c: '#B9C0C8', top: '#9AA1AA' });
      break;
    case 'sculpture':
      pieces.push({
        s: 'box',
        x: x - 0.3,
        y: y - 0.3,
        w: 0.6,
        d: 0.6,
        z: 0,
        h: 0.12,
        c: '#9EA19C',
        roof: true,
      });
      pieces.push({
        s: 'box',
        x: x - 0.18,
        y: y - 0.18,
        w: 0.36,
        d: 0.36,
        z: 0.12,
        h: 0.36,
        c: C.violet,
      });
      pieces.push({
        s: 'box',
        x: x - 0.1,
        y: y - 0.12,
        w: 0.26,
        d: 0.26,
        z: 0.48,
        h: 0.3,
        c: C.blue,
      });
      pieces.push({
        s: 'box',
        x: x - 0.06,
        y: y - 0.04,
        w: 0.16,
        d: 0.16,
        z: 0.78,
        h: 0.22,
        c: C.yellow,
      });
      break;
    case 'flagpole':
      pieces.push({ s: 'cyl', x, y, r: 0.03, z: 0, h: 1.5, c: '#E4E7EA' });
      pieces.push({
        s: 'box',
        x: x + 0.03,
        y: y - 0.01,
        w: 0.45,
        d: 0.02,
        z: 1.18,
        h: 0.28,
        c: '#3E6FB0',
      });
      break;
    case 'footbridge':
      for (const dx of [-0.55, 0.45])
        pieces.push({
          s: 'box',
          x: x + dx,
          y: y - 0.15,
          w: 0.1,
          d: 0.3,
          z: 0,
          h: 0.5,
          c: C.light,
          roof: true,
        });
      pieces.push({
        s: 'box',
        x: x - 0.6,
        y: y - 0.15,
        w: 1.2,
        d: 0.3,
        z: 0.5,
        h: 0.06,
        c: C.mid,
        top: '#B9BFC6',
        roof: true,
      });
      pieces.push({
        s: 'rod',
        a: [x - 0.6, y - 0.15, 0.68],
        b: [x + 0.6, y - 0.15, 0.68],
        t: 0.02,
        c: C.steel,
      });
      pieces.push({
        s: 'rod',
        a: [x - 0.6, y + 0.15, 0.68],
        b: [x + 0.6, y + 0.15, 0.68],
        t: 0.02,
        c: C.steel,
      });
      break;
    case 'beacon':
      pieces.push({ s: 'box', x: x - 0.1, y: y - 0.1, w: 0.2, d: 0.2, z: 0, h: 1.2, c: '#3B4048' });
      pieces.push({ s: 'cyl', x, y, r: 0.13, z: 1.2, h: 0.16, c: C.cyan, glow: true });
      break;
    default:
      pieces.push({
        s: 'box',
        x: x - 0.25,
        y: y - 0.25,
        w: 0.5,
        d: 0.5,
        z: 0,
        h: 0.4,
        c: '#9EA19C',
        roof: true,
      });
  }
  return [{ x: x - MH, y: y - MH, w: MH * 2, d: MH * 2, pieces }];
}

/* ---------- heights, for the camera and a still's fit ---------- */

/** How tall a piece stands over the ground. */
export function pieceTop(q: Piece): number {
  switch (q.s) {
    case 'rod':
      return Math.max(q.a[2], q.b[2]);
    case 'panel':
      return q.z + q.rise;
    case 'disc':
      return q.z;
    case 'ball':
      return q.z + q.r * 2;
    default:
      return q.z + q.h;
  }
}

export function unitTop(u: Unit): number {
  let t = 0;
  for (const q of u.pieces) t = Math.max(t, pieceTop(q));
  for (const q of u.fine ?? []) t = Math.max(t, pieceTop(q));
  return t;
}
